/**
 * Phase 8.3 — Scorecard against exported boot telemetry reports.
 * Objective checks from PHASE8-VERIFICATION-PROTOCOL (Sessions A–E).
 * No architecture refactor — verification tooling only.
 */

export const PHASE8_SESSIONS = Object.freeze(['A', 'B', 'C', 'D', 'E']);

/** Protocol download basename prefixes (date appended by caller). */
export const PHASE8_SESSION_FILENAMES = Object.freeze({
  A: 'p8-boot-pos',
  B: 'p8-manager-mount',
  C: 'p8-stock-mount',
  D: 'p8-sale-commit',
  E: 'p8-purchase-commit'
});

function check(id, pass, detail = '') {
  return { id, pass: Boolean(pass), detail: detail || (pass ? 'ok' : 'fail') };
}

function timelineGates(report) {
  return new Set((report?.timeline || []).map((t) => t.gate).filter(Boolean));
}

function msOf(report, key) {
  const v = report?.summary?.[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function hasOp(report, name) {
  return (report?.operations || []).some((o) => o?.name === name);
}

function validateSessionA(report) {
  const gates = report?.adr0014Gates || {};
  const summary = report?.summary || {};
  const timeline = timelineGates(report);
  const checks = [];

  checks.push(
    check('A.shellReady', gates.shellReady === true, `shellReady=${gates.shellReady}`)
  );
  checks.push(
    check(
      'A.surfaceDataReady',
      gates.surfaceDataReady === true,
      `surfaceDataReady=${gates.surfaceDataReady}`
    )
  );
  checks.push(
    check(
      'A.transactionReady',
      gates.transactionReady === true,
      `transactionReady=${gates.transactionReady}`
    )
  );

  // Allow ~100ms mark race: transaction_ready can fire just before dashboard_mounted
  // when POS data readiness and route mount resolve in the same tick.
  const TX_DASH_RACE_MS = 100;
  const msTx = msOf(report, 'msToTransactionReady');
  const msDash = msOf(report, 'msToDashboardMount');
  if (msTx != null && msDash != null) {
    const ok = msTx + TX_DASH_RACE_MS >= msDash;
    checks.push(
      check(
        'A.txAfterDashboardMount',
        ok,
        `msToTransactionReady=${msTx} msToDashboardMount=${msDash} (race≤${TX_DASH_RACE_MS}ms)`
      )
    );
  } else {
    checks.push(
      check(
        'A.txAfterDashboardMount',
        msTx != null,
        `msToTransactionReady=${msTx} msToDashboardMount=${msDash}`
      )
    );
  }

  const blocked = Number(summary.checkoutBlocked) || 0;
  checks.push(check('A.checkoutBlockedZero', blocked === 0, `checkoutBlocked=${blocked}`));

  // Worker POS: manager_mounted must not appear before dashboard_interactive
  const interactive = (report?.timeline || []).find((t) => t.gate === 'dashboard_interactive');
  const manager = (report?.timeline || []).find((t) => t.gate === 'manager_mounted');
  if (interactive && manager) {
    const ok = manager.msSinceBootstrap >= interactive.msSinceBootstrap;
    checks.push(
      check(
        'A.noManagerBeforeInteractive',
        ok,
        `manager_mounted@${manager.msSinceBootstrap} vs dashboard_interactive@${interactive.msSinceBootstrap}`
      )
    );
  } else {
    checks.push(
      check(
        'A.noManagerBeforeInteractive',
        !timeline.has('manager_mounted'),
        timeline.has('manager_mounted')
          ? 'manager_mounted without dashboard_interactive'
          : 'no premature manager_mounted'
      )
    );
  }

  return checks;
}

function validateSessionB(report) {
  const timeline = timelineGates(report);
  const ms = msOf(report, 'msToManagerMount');
  return [
    check('B.managerMounted', timeline.has('manager_mounted'), 'manager_mounted in timeline'),
    check('B.msToManagerMount', ms != null, `msToManagerMount=${ms}`)
  ];
}

function validateSessionC(report) {
  const timeline = timelineGates(report);
  const ms = msOf(report, 'msToStockMount');
  return [
    check('C.stockMounted', timeline.has('stock_mounted'), 'stock_mounted in timeline'),
    check('C.msToStockMount', ms != null, `msToStockMount=${ms}`)
  ];
}

function validateSessionD(report) {
  const saleCommits = Number(report?.summary?.saleCommits) || 0;
  return [
    check('D.saleCommitOp', hasOp(report, 'sale_commit'), 'operations contains sale_commit'),
    check('D.saleCommits', saleCommits >= 1, `saleCommits=${saleCommits}`)
  ];
}

/**
 * Session E: purchase_commit preferred; soft-warn if only counter missing
 * but op present (or vice versa).
 */
function validateSessionE(report) {
  const purchaseCommits = Number(report?.summary?.purchaseCommits) || 0;
  const hasPurchaseOp = hasOp(report, 'purchase_commit');
  const checks = [];

  if (hasPurchaseOp) {
    checks.push(check('E.purchaseCommitOp', true, 'operations contains purchase_commit'));
    checks.push(
      check(
        'E.purchaseCommits',
        purchaseCommits >= 1,
        purchaseCommits >= 1
          ? `purchaseCommits=${purchaseCommits}`
          : `soft: op present but purchaseCommits=${purchaseCommits}`
      )
    );
    // Soft: counter missing is still overall fail for that check, but ok if op exists
    // — plan says soft-fail when counter missing. Mark soft check as pass with warning detail.
    if (purchaseCommits < 1) {
      checks[checks.length - 1] = check(
        'E.purchaseCommits',
        true,
        `soft-pass: purchase_commit op present, counter=${purchaseCommits}`
      );
    }
  } else if (purchaseCommits >= 1) {
    checks.push(
      check(
        'E.purchaseCommitOp',
        true,
        'soft-pass: purchaseCommits≥1 without named op (legacy path)'
      )
    );
    checks.push(check('E.purchaseCommits', true, `purchaseCommits=${purchaseCommits}`));
  } else {
    checks.push(check('E.purchaseCommitOp', false, 'no purchase_commit operation'));
    checks.push(check('E.purchaseCommits', false, `purchaseCommits=${purchaseCommits}`));
  }

  return checks;
}

const SESSION_VALIDATORS = {
  A: validateSessionA,
  B: validateSessionB,
  C: validateSessionC,
  D: validateSessionD,
  E: validateSessionE
};

/**
 * @param {object} report — bootTelemetry.export() shape
 * @param {string} [session] — A|B|C|D|E|all
 * @returns {{ session: string, ok: boolean, checks: Array<{id,pass,detail}>, summary: object, sessions?: object }}
 */
export function validatePhase8Report(report, session = 'A') {
  const key = String(session || 'A').toUpperCase();

  if (key === 'ALL') {
    const sessions = {};
    let ok = true;
    const allChecks = [];
    for (const s of PHASE8_SESSIONS) {
      const result = validatePhase8Report(report, s);
      sessions[s] = result;
      ok = ok && result.ok;
      allChecks.push(...result.checks);
    }
    return {
      session: 'all',
      ok,
      checks: allChecks,
      summary: report?.summary || {},
      sessions
    };
  }

  const validator = SESSION_VALIDATORS[key];
  if (!validator) {
    return {
      session: key,
      ok: false,
      checks: [check('session.valid', false, `unknown session "${session}"`)],
      summary: report?.summary || {}
    };
  }

  const phaseOk =
    !report?.phase || report.phase === 'adr-0014-phase-8'
      ? check('phase', true, report?.phase || 'missing (accepted)')
      : check('phase', false, `unexpected phase=${report.phase}`);

  const checks = [phaseOk, ...validator(report)];
  const ok = checks.every((c) => c.pass);

  return {
    session: key,
    ok,
    checks,
    summary: {
      msToShellReady: report?.summary?.msToShellReady ?? null,
      msToSurfaceDataReady: report?.summary?.msToSurfaceDataReady ?? null,
      msToTransactionReady: report?.summary?.msToTransactionReady ?? null,
      msToDashboardMount: report?.summary?.msToDashboardMount ?? null,
      msToManagerMount: report?.summary?.msToManagerMount ?? null,
      msToStockMount: report?.summary?.msToStockMount ?? null,
      saleCommits: report?.summary?.saleCommits ?? 0,
      purchaseCommits: report?.summary?.purchaseCommits ?? 0,
      checkoutBlocked: report?.summary?.checkoutBlocked ?? 0
    }
  };
}

/**
 * @param {string} session
 * @param {string} [iso]
 */
export function phase8SessionFilename(session, iso = null) {
  const key = String(session || 'A').toUpperCase();
  const prefix = PHASE8_SESSION_FILENAMES[key] || `p8-session-${key.toLowerCase()}`;
  const stamp = (iso || new Date().toISOString()).replace(/[:.]/g, '-');
  return `${prefix}-${stamp}.json`;
}

export default {
  validatePhase8Report,
  phase8SessionFilename,
  PHASE8_SESSIONS,
  PHASE8_SESSION_FILENAMES
};
